import {
  AddDisplayLocationDto,
  DisplayEntity,
  DisplayEventPayload,
  DisplayMetadata,
  FailManualScreenshotDto,
  SetDisplayBrowserMetadataDataDto,
} from "@admefy/domain";
import { inject, Injectable } from "@angular/core";
import { HttpErrorResponse } from "@angular/common/http";
import {
  catchError,
  EMPTY,
  from,
  map,
  Observable,
  of,
  retry,
  switchMap,
  throwError,
  timer,
  timeout,
  TimeoutError,
} from "rxjs";
import { getBrowserMetadata } from "../../utils/get-browser-metadata";
import { RestService } from "../admefy-cli/admefy-cli.service";
import { MetadataService } from "../metadata/metadata.service";

interface NewQrDisplayRequest {
  socketId: string;
  providerId?: string;
  ownerId?: string;
  metadata: DisplayMetadata;
}

@Injectable({
  providedIn: "root",
})
export class DisplayService {
  private readonly metadataService = inject(MetadataService);
  private readonly rest = inject(RestService);

  public newQRTokenDisplay({
    socketId,
    providerId,
    ownerId,
    position,
    address,
  }: {
    socketId: string;
    providerId?: string;
    ownerId?: string;
    position: GeolocationPosition | null;
    address?: {
      fullAddress?: string;
      country?: string;
      city?: string;
      locality?: string;
      postcode?: string;
    } | null;
  }): Observable<{ token: string }> {
    return from(getBrowserMetadata()).pipe(
      switchMap((browserMetadata) => {
        return this.metadataService.getGeoData().pipe(
          switchMap((metadata) => {
            const data: NewQrDisplayRequest = {
              ownerId,
              socketId,
              providerId,
              metadata: {
                city: address?.city || metadata.city,
                country: address?.country || metadata.country_code,
                countryName: address?.country || metadata.country,
                address: address?.fullAddress,
                continentCode: metadata.continent,
                currentIp: metadata.ip,
                ip: metadata.ip,
                latitude: metadata.latitude,
                longitude: metadata.longitude,
                timezone: metadata.timezone,
                windowData: browserMetadata?.windowData || undefined,
                location: {
                  type: "Point",
                  coordinates: [
                    Number(position?.coords.longitude || metadata.longitude) || 0,
                    Number(position?.coords.latitude || metadata.latitude) || 0,
                  ],
                },
              },
            };

            return this.rest.post(`/display/qr`, data).pipe(
              map((res) => res.body?.data),
              retry(2),
            );
          }),
        );
      }),
    );
  }

  getQRUrlImg(linkCode: string): string {
    return `${this.rest.apiUrl}/display/qr?content=${linkCode}`;
  }

  getDisplayToken(id: string): Observable<string> {
    return this.rest.get(`/display/${id}`).pipe(
      map((res) => {
        return res.body?.data;
      }),
    );
  }

  getDisplay(): Observable<DisplayEntity> {
    return this.rest.get(`/display`).pipe(
      map((res) => {
        return res.body?.data;
      }),
    );
  }

  saveSocketId(socketId: string): Observable<void> {
    return this.rest.post(`/display/socket`, { socketId }).pipe(
      map(() => {
        return;
      }),
    );
  }

  addDisplayLocations(body: AddDisplayLocationDto): Observable<void> {
    return this.rest.post(`/display-locations`, body).pipe(
      catchError(() => EMPTY),
      map(() => {
        return;
      }),
    );
  }

  getCustomScreenSaver(): Observable<{
    url: string;
    duration: number;
    subscription: any;
  } | null> {
    return this.rest.get(`/display/screensaver`).pipe(
      map((res) => {
        return res.body?.data;
      }),
    );
  }

  setDisplayBrowserMetadata(data: SetDisplayBrowserMetadataDataDto): Observable<void> {
    return this.rest.patch(`/display/metadata/browser`, { ...data, playerType: "player-web" }).pipe(
      timeout({ first: 10_000 }),
      retry({
        count: 2,
        delay: (error, attempt) => {
          if (
            error instanceof TimeoutError ||
            (error instanceof HttpErrorResponse &&
              (error.status === 0 ||
                error.status === 408 ||
                error.status === 429 ||
                error.status >= 500))
          ) {
            return timer(1000 * 2 ** (attempt - 1));
          }
          return throwError(() => error);
        },
      }),
      map(() => {
        return;
      }),
      catchError((error) => {
        console.warn("Could not register web player metadata", error?.status ?? error?.name);
        return EMPTY;
      }),
    );
  }

  createDisplayEvent(data: DisplayEventPayload): Observable<void> {
    return this.rest.post("/display/events", data).pipe(
      map(() => undefined),
      catchError(() => of(undefined)),
    );
  }

  failManualScreenshot(data: FailManualScreenshotDto): Observable<void> {
    return this.rest.post("/display/screenshots/manual/fail", data).pipe(
      map(() => undefined),
      catchError(() => of(undefined)),
    );
  }
}
